/* eslint-disable react/sort-comp */
import { Button, IllustratedMessage, Progress, SearchField, Stack } from '@eleveo/components-react';
import { FilterIcon, SearchNoneIcon } from '@eleveo/icons-react';
import debounce from 'lodash.debounce';
import { equals } from 'ramda';
import React, { useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useDateFormatter } from 'react-aria';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Permissions, useAuth } from '../../app/AuthorizationProvider';
import { Header } from '../../components/LayoutNext/Header';
import { Content, Panel, SidebarWrapper } from '../../components/LayoutNext/LayoutNext';
import { ActiveFilters } from '../../components/LayoutNext/components/ActiveFilters';
import { Center } from '../../components/LayoutNext/components/Center';
import { getActiveFilterValue } from '../../components/LayoutNext/components/CheckboxFilterTile';
import { ModalConfirmationNewStyle as ModalConfirmation } from '../../components/ModalConfirmationNewStyle';
import { useSidebar } from '../../components/layout/Sidebar';
import { DEFAULT_FILTERS_DEBOUNCE_TIMEOUT_IN_MS } from '../../constants';
import {
  Forecast,
  GetForecastsDocument,
  useGetForecastsQuery,
  useDeleteForecastMutation,
} from '../../graphql/components';
import { PATH_FORECASTING_STUDIO_CREATE } from '../../router/routes';
import { useFilter } from '../../utils/filter';
import { ForecastingStudioFilterSidebar } from './ForecastingStudioFilterSidebar';
import { ForecastsTable } from './ForecastsTable';
import { FILTER_QUEUE_INPUT, STUDIO_TITLE_TAG, TOGGLE_FILTER_BUTTON_TAG } from './constants';

import type { DateRangeFilter } from '../../utils/filter';
import type { ForecastingStudioFilterSidebarState } from './ForecastingStudioFilterSidebar';

const defaultSidebarFilter = {
  range: null as unknown as DateRangeFilter,
  queues: [],
};

export const ForecastingStudioScene = () => {
  const { timezone, userHasPermissions } = useAuth();
  const { closeSidebar, isSidebarOpen, openSidebar, toggleSidebar } = useSidebar();
  const { t } = useTranslation();
  const formatter = useDateFormatter({ dateStyle: 'short', timeZone: timezone });

  const [expandedFilterItemIds, setExpandedFilterItemIds] = useState<string[]>([]);
  const [forecastToDelete, setForecastToDelete] = useState<Forecast>();

  const {
    filter: searchFilter,
    setFilter: setSearchFilter,
    isFilterActive: isSearchFilterActive,
  } = useFilter({
    forecastName: '',
  });
  const { filter: sidebarFilter, setFilter: setSidebarFilter } =
    useFilter<ForecastingStudioFilterSidebarState>(defaultSidebarFilter);

  const forecastsFilter = {
    queueId: sidebarFilter.queues[0],
    forecastName: searchFilter.forecastName,
    forecastStartDateTime: sidebarFilter.range?.start ? sidebarFilter.range.start.toDate(timezone).toISOString() : '',
    forecastEndDateTime: sidebarFilter.range?.end
      ? sidebarFilter.range.end.add({ days: 1 }).toDate(timezone).toISOString()
      : '',
  };

  const {
    data: { forecasts, queues } = { forecasts: [], queues: [] },
    error,
    loading,
  } = useGetForecastsQuery({
    fetchPolicy: 'network-only',
    variables: forecastsFilter,
  });

  const [deleteForecast, { loading: deleteForecastLoading }] = useDeleteForecastMutation();

  const mutationInProgress = deleteForecastLoading;
  const skipRendering = (loading && !forecasts.length) || error;

  const hasCreatePermissions = userHasPermissions([Permissions.CreateForecast]);

  const handleSearch = (value: string) => {
    setSearchFilter({ forecastName: value.trim() });
  };

  const handleToggleFilterSidebar = () => {
    setExpandedFilterItemIds([]);
    toggleSidebar();
  };

  const handleOpenFilterSidebarItem = (preOpenItemId: string) => {
    setExpandedFilterItemIds([preOpenItemId]);
    openSidebar();
  };

  const handleCancelFilterSidebar = () => {
    closeSidebar();
    setExpandedFilterItemIds([]);
  };

  const handleDelete = async () => {
    try {
      if (!forecastToDelete) {
        return;
      }

      await deleteForecast({
        variables: {
          id: forecastToDelete.id,
        },
        refetchQueries: [
          {
            query: GetForecastsDocument,
            variables: forecastsFilter,
          },
        ],
      });
    } catch (e) {
      // `customFetch()` will display error message
    } finally {
      setForecastToDelete(undefined);
    }
  };

  return (
    <>
      <Header title={t('forecast.forecastingStudio')} testId={STUDIO_TITLE_TAG} />

      <Panel>
        <Stack direction="horizontal" className="w-full" spacing={15}>
          {hasCreatePermissions && (
            <Button as={Link} testId="list-filter:button-create" to={PATH_FORECASTING_STUDIO_CREATE}>
              {t('forecast.newForecast')}
            </Button>
          )}

          <Stack>
            <SearchField
              placeholder={t('form.name')}
              defaultValue={searchFilter.forecastName}
              onChange={debounce(handleSearch, DEFAULT_FILTERS_DEBOUNCE_TIMEOUT_IN_MS)}
              onClear={() => handleSearch('')}
              onSubmit={handleSearch}
              testId={FILTER_QUEUE_INPUT}
            />
          </Stack>
          <ActiveFilters
            config={[
              {
                dataTestId: 'forecasts-active-sidebar-filter-range',
                defaultValue: null,
                id: '1',
                key: 'range',
                name: t('forecast.forecastRange'),
                value:
                  sidebarFilter.range?.start && sidebarFilter.range?.end
                    ? `${formatter.format(sidebarFilter.range.start?.toDate(timezone))} - ${formatter.format(
                        sidebarFilter.range?.end.toDate(timezone)
                      )}`
                    : '',
              },
              {
                dataTestId: 'forecasts-active-sidebar-filter-queues',
                defaultValue: [],
                id: '2',
                key: 'queues',
                name: t('queue.queue'),
                value: getActiveFilterValue(queues, sidebarFilter.queues),
              },
            ]}
            sidebarFilter={sidebarFilter}
            setSidebarFilter={setSidebarFilter}
            openFilterSidebarItem={handleOpenFilterSidebarItem}
            cancelFilterSidebar={handleCancelFilterSidebar}
          />

          <Button
            isSelected={isSidebarOpen}
            className="mis-auto"
            testId={TOGGLE_FILTER_BUTTON_TAG}
            onClick={handleToggleFilterSidebar}
          >
            <FilterIcon />
            {t('filter.filters')}
          </Button>
        </Stack>
      </Panel>

      <Content testId="forecasting-studio:container">
        {(loading || mutationInProgress) && <Progress withOverlay />}

        {!skipRendering && forecasts.length > 0 && (
          <ForecastsTable forecasts={forecasts} queues={queues} onDelete={setForecastToDelete} />
        )}

        {!skipRendering && forecasts.length === 0 && (
          <Center>
            <IllustratedMessage
              heading={
                isSearchFilterActive || !equals(defaultSidebarFilter, sidebarFilter)
                  ? t('filter.noResultsMatch')
                  : t('forecast.noEntries')
              }
              icon={<SearchNoneIcon />}
            />
          </Center>
        )}

        <ModalConfirmation
          acceptCaption={t('layout.action.delete')}
          cancelCaption={t('layout.action.cancel')}
          isOpen={!!forecastToDelete}
          onAccept={handleDelete}
          onCancel={() => setForecastToDelete(undefined)}
          title={t('forecast.delete')}
        >
          {t('forecast.deleteBody', { forecastName: forecastToDelete?.name })}
        </ModalConfirmation>
      </Content>

      <SidebarWrapper>
        {isSidebarOpen && (
          <ForecastingStudioFilterSidebar
            queues={queues}
            preOpenedItemIds={expandedFilterItemIds}
            setPreOpenedItemIds={setExpandedFilterItemIds}
            sidebarFilter={sidebarFilter}
            setSidebarFilter={setSidebarFilter}
            handleCancel={handleCancelFilterSidebar}
          />
        )}
      </SidebarWrapper>
    </>
  );
};
